import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { TmBaseComponent } from '../tm-base.component';

export const tagName = 'tm-switch';
@customElement(tagName)
export class TmSwitch extends TmBaseComponent {
    constructor() {
        super();
        this.addEventListener('click', event => {
            if (this.disabled) {
                event.preventDefault();
                event.stopImmediatePropagation();
                event.stopPropagation();
            }
        });
    }

    @property({ type: Boolean, attribute: true, reflect: true })
    public disabled = false;

    @property({ type: Boolean, attribute: true, reflect: true })
    public checked = false;

    @property({ type: Boolean, attribute: true, reflect: true })
    public horizontal = false;

    @property({ type: String })
    public label?: string;

    protected updated(_changedProperties: Map<PropertyKey, unknown>): void {
        if (_changedProperties.has('label')) {
            if (this.label) {
                this.setAttribute('has-label', '');
            } else {
                this.removeAttribute('has-label');
            }
        }
    }

    protected render() {
        return html`
            <div
                class=${classMap({
                    'inline-block': !this.horizontal,
                    'flex w-full justify-between items-center': this.horizontal,
                })}
            >
                ${this.renderLabel()}
                <div id="tmSwitch">
                    <input
                        id="checkbox"
                        aria-label=${this.label || ''}
                        type="checkbox"
                        .checked=${this.checked}
                        ?disabled=${this.disabled}
                        @change=${(e: Event) => this.changeEvent(e)}
                    />
                    <label for="checkbox" id="switch">
                        <span></span>
                    </label>
                </div>
            </div>
        `;
    }

    private changeEvent(e: Event) {
        this.checked = (e.target as HTMLInputElement).checked;
        const checkedChangedEvent = new CustomEvent('checked-changed', {
            composed: true,
            bubbles: true,
            detail: {
                checked: this.checked,
                label: this.label,
            },
        });
        this.dispatchEvent(checkedChangedEvent);
    }

    private renderLabel() {
        if (!this.label) {
            return nothing;
        }
        return html`<label for="tmSwitch">${this.label}</label>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: inline-block;
                    --switch-checked: var(--brand-primary);
                    --switch-unchecked: var(--tm-color-gray-light);
                    --switch-checked-focus-aura: var(--primary-30);
                    --switch-unchecked-focus-aura: var(--tm-color-gray-light-30);
                }

                :host([theme='dark']) {
                    --switch-checked: var(--brand-100);
                    --switch-unchecked: var(--tm-color-gray-light-15);
                    --switch-checked-focus-aura: var(--primary-30);
                    --switch-unchecked-focus-aura: var(--tm-color-gray-light-15);
                }

                :host([disabled]) label[for='tmSwitch'] {
                    color: var(--tertiary-text-color);
                    -webkit-text-fill-color: var(--tertiary-text-color);
                }

                :host([has-label]) {
                    padding-top: var(--lumo-space-m);
                }

                label[for='tmSwitch'] {
                    color: var(--secondary-text-color);
                    font-weight: 500;
                    font-size: var(--lumo-font-size-s);
                    line-height: 1;
                    white-space: nowrap;
                }

                :host(.form-row-slotted) {
                    text-align: right;
                }

                :host(.form-row-slotted) #switch {
                    display: inline-block;
                    margin: 8px 0 10px;
                }

                #switch {
                    position: relative;
                    display: inline-block;
                    margin: 0;
                    margin-left: -4px;
                    padding: 4px;
                    cursor: pointer;
                }

                input {
                    position: absolute;
                    margin-left: -9999px;
                    opacity: 0;
                }
                span {
                    display: inline-block;
                    position: relative;
                    outline: none;
                    user-select: none;
                    pointer-events: none;
                    vertical-align: middle;
                    width: 56px;
                    height: 26px;
                    border-radius: 12px;
                    padding: 1px;
                    transition: background-color 0.3s cubic-bezier(0.1, 0.5, 0.1, 1);
                    box-sizing: border-box;
                }
                span::before,
                span::after {
                    content: '';
                    position: absolute;
                    box-sizing: border-box;
                }
                span::before {
                    top: 1px;
                    bottom: 1px;
                    left: 1px;
                    right: 1px;
                    background-color: var(--switch-unchecked);
                    border-radius: 13px;
                    transition: background-color 0.3s cubic-bezier(0.1, 0.5, 0.1, 1);
                }
                span::after {
                    top: 3px;
                    bottom: 0;
                    left: 3px;
                    width: 20px;
                    height: 20px;
                    background-color: var(--jh-color-gray-0, #ffffff);
                    border: none;
                    border-radius: 100%;
                    box-shadow: 0 2px 2px 0px rgba(0, 0, 0, 0.5);
                    transition: all 0.3s cubic-bezier(0.1, 0.5, 0.1, 1);
                }
                :host([disabled]) span::before {
                    opacity: 0.4;
                }
                :host([disabled]:not([checked])) span::after {
                    background-color: rgba(255, 255, 255, 0.6);
                }
                :host([disabled]) label {
                    cursor: default;
                }
                :host([checked]) span::before {
                    background-color: var(--switch-checked);
                }
                :host([checked]) span::after {
                    transform: translateX(30px);
                }
                :host([checked][disabled]) span::before {
                    opacity: 0.4;
                    background-color: var(--switch-checked);
                }

                input:focus + #switch span::after {
                    box-shadow: 0 0 0px 10px var(--switch-unchecked-focus-aura);
                }
                :host([checked]) input:focus + #switch span::after {
                    box-shadow: 0 0 0px 10px var(--switch-checked-focus-aura);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmSwitch;
    }
}
